import style from '@/constants/colors.module.scss';
import { Color } from '@/model/Common.model';

export interface ColorDetail {
  color: Color;
  background?: { color: Color; class: string };
}

interface Colors {
  color_01: ColorDetail;
  color_02: ColorDetail;
  color_03: ColorDetail;
  color_04: ColorDetail;
  color_05: ColorDetail;
  color_06: ColorDetail;
  color_07: ColorDetail;
  color_08: ColorDetail;
  color_09: ColorDetail;
  color_10: ColorDetail;
  color_11: ColorDetail;
  color_12: ColorDetail;
  color_13: ColorDetail;
  color_14: ColorDetail;
  color_15: ColorDetail;
  color_16: ColorDetail;
  color_17: ColorDetail;
  color_18: ColorDetail;
  color_19: ColorDetail;
  color_20: ColorDetail;
  color_21: ColorDetail;
  color_22: ColorDetail;
  color_23: ColorDetail;
  color_24: ColorDetail;
}

export type ColorKey = keyof Colors;
export const colors: Colors = {
  color_01: {
    color: style.color_01 as Color,
    background: { color: style.color_01_bg as Color, class: style.bg_color_01 },
  },
  color_02: {
    color: style.color_02 as Color,
    background: { color: style.color_02_bg as Color, class: style.bg_color_02 },
  },
  color_03: {
    color: style.color_03 as Color,
    background: { color: style.color_03_bg as Color, class: style.bg_color_03 },
  },
  color_04: {
    color: style.color_04 as Color,
    background: { color: style.color_04_bg as Color, class: style.bg_color_04 },
  },
  color_05: {
    color: style.color_05 as Color,
    background: { color: style.color_05_bg as Color, class: style.bg_color_05 },
  },
  color_06: {
    color: style.color_06 as Color,
    background: { color: style.color_06_bg as Color, class: style.bg_color_06 },
  },
  color_07: {
    color: style.color_07 as Color,
    background: { color: style.color_07_bg as Color, class: style.bg_color_07 },
  },
  color_08: {
    color: style.color_08 as Color,
    background: { color: style.color_08_bg as Color, class: style.bg_color_08 },
  },
  color_09: {
    color: style.color_09 as Color,
    background: { color: style.color_09_bg as Color, class: style.bg_color_09 },
  },
  color_10: {
    color: style.color_10 as Color,
    background: { color: style.color_10_bg as Color, class: style.bg_color_10 },
  },
  color_11: {
    color: style.color_11 as Color,
    background: { color: style.color_11_bg as Color, class: style.bg_color_11 },
  },
  color_12: {
    color: style.color_12 as Color,
    background: { color: style.color_12_bg as Color, class: style.bg_color_12 },
  },
  color_13: {
    color: style.color_13 as Color,
    background: { color: style.color_13_bg as Color, class: style.bg_color_13 },
  },
  color_14: {
    color: style.color_14 as Color,
    background: { color: style.color_14_bg as Color, class: style.bg_color_14 },
  },
  color_15: {
    color: style.color_15 as Color,
    background: { color: style.color_15_bg as Color, class: style.bg_color_15 },
  },
  color_16: {
    color: style.color_16 as Color,
    background: { color: style.color_16_bg as Color, class: style.bg_color_16 },
  },
  color_17: {
    color: style.color_17 as Color,
    background: { color: style.color_17_bg as Color, class: style.bg_color_17 },
  },
  color_18: {
    color: style.color_18 as Color,
    background: { color: style.color_18_bg as Color, class: style.bg_color_18 },
  },
  color_19: {
    color: style.color_19 as Color,
    background: { color: style.color_19_bg as Color, class: style.bg_color_19 },
  },
  color_20: {
    color: style.color_20 as Color,
    background: { color: style.color_20_bg as Color, class: style.bg_color_20 },
  },
  color_21: {
    color: style.color_21 as Color,
    background: { color: style.color_21_bg as Color, class: style.bg_color_21 },
  },
  color_22: {
    color: style.color_22 as Color,
    background: { color: style.color_22_bg as Color, class: style.bg_color_22 },
  },
  color_23: {
    color: style.color_23 as Color,
    background: { color: style.color_23_bg as Color, class: style.bg_color_23 },
  },
  color_24: {
    color: style.color_24 as Color,
    background: { color: style.color_24_bg as Color, class: style.bg_color_24 },
  },
};
export const defaultColor: ColorDetail = { color: style.default_blue as Color };
export const colorKeys = Object.keys(colors) as Array<ColorKey>;
export const colorSwatch = Object.values(colors).map((details: ColorDetail) => details.color) as Array<Color>;

export const getColorDetail = (key: ColorKey): ColorDetail => colors[key];

export interface AvatarColor {
  border: Color;
  fill: Color;
}

const AVATAR_INDEXES = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10'] as const;
export const avatarSwatch: ReadonlyArray<AvatarColor> = AVATAR_INDEXES.map((idx) => ({
  border: style[`avatar_${idx}`] as Color,
  fill: style[`avatar_${idx}_fill`] as Color,
}));

interface Oklch {
  L: number;
  C: number;
  h: number;
}

const srgbChannelToLinear = (channel: number): number =>
  channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);

const parseHex = (hex: string): { r: number; g: number; b: number } => {
  const trimmed = hex.startsWith('#') ? hex.slice(1) : hex;
  const expanded =
    trimmed.length === 3
      ? trimmed
          .split('')
          .map((c) => c + c)
          .join('')
      : trimmed;
  const intValue = Number.parseInt(expanded, 16);
  return {
    r: ((intValue >> 16) & 0xff) / 255,
    g: ((intValue >> 8) & 0xff) / 255,
    b: (intValue & 0xff) / 255,
  };
};

/**
 * Converts an sRGB hex color to OKLCH using Björn Ottosson's formulas.
 * Result hue is in radians.
 */
export const hexToOklch = (hex: string): Oklch => {
  const { r, g, b } = parseHex(hex);
  const lr = srgbChannelToLinear(r);
  const lg = srgbChannelToLinear(g);
  const lb = srgbChannelToLinear(b);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const lCbrt = Math.cbrt(l);
  const mCbrt = Math.cbrt(m);
  const sCbrt = Math.cbrt(s);

  const lab_L = 0.2104542553 * lCbrt + 0.793617785 * mCbrt - 0.0040720468 * sCbrt;
  const lab_a = 1.9779984951 * lCbrt - 2.428592205 * mCbrt + 0.4505937099 * sCbrt;
  const lab_b = 0.0259040371 * lCbrt + 0.7827717662 * mCbrt - 0.808675766 * sCbrt;

  const C = Math.hypot(lab_a, lab_b);
  const h = Math.atan2(lab_b, lab_a);
  return { L: lab_L, C, h };
};

const avatarSwatchOklch: ReadonlyArray<Oklch> = avatarSwatch.map((swatch) => hexToOklch(swatch.border));

/**
 * Polar-aware perceptual distance between two OKLCH colors.
 */
export const oklchDistance = (a: Oklch, b: Oklch): number => {
  const dL = a.L - b.L;
  const cosTerm = 2 * a.C * b.C * Math.cos(a.h - b.h);
  return Math.sqrt(dL * dL + a.C * a.C + b.C * b.C - cosTerm);
};

const hashStringToIndex = (input: string, modulo: number): number => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return (hash >>> 0) % modulo;
};

/**
 * Picks the index of the avatar swatch entry whose border is farthest (in OKLCH)
 * from every already-assigned entry. `usedIndices` holds the swatch indices that
 * have already been handed out, so each pick is a scan over the ≤10 precomputed
 * swatch OKLCH values with no per-call allocation. When every swatch entry is in
 * use, falls back to a deterministic hash of `seed` so the same identifier always
 * maps to the same fallback entry.
 */
export const pickFarthestAvatarColorIndex = (usedIndices: ReadonlySet<number>, seed: string): number => {
  if (usedIndices.size === 0) {
    return 0;
  }
  let bestIndex = -1;
  let bestScore = -Infinity;
  for (let candidateIndex = 0; candidateIndex < avatarSwatchOklch.length; candidateIndex++) {
    if (usedIndices.has(candidateIndex)) {
      continue;
    }
    const candidateOklch = avatarSwatchOklch[candidateIndex];
    let minDistance = Infinity;
    for (const usedIndex of usedIndices) {
      const distance = oklchDistance(candidateOklch, avatarSwatchOklch[usedIndex]);
      if (distance < minDistance) {
        minDistance = distance;
      }
    }
    if (minDistance > bestScore) {
      bestScore = minDistance;
      bestIndex = candidateIndex;
    }
  }
  if (bestIndex === -1) {
    return hashStringToIndex(seed, avatarSwatch.length);
  }
  return bestIndex;
};
